import { SyntheticEvent } from 'react';
import classNames from 'classnames';
import { TableBodyColumn } from 'ui/components/table';
import TableBodyRow from 'ui/components/table/TableBodyRow';
import { EditButton, RemoveButton } from 'dashboard/components/table/buttons';
import TariffGroupItem from 'tariffGroups/components/TariffGroupItem';
import { TariffGroupProps } from 'tariffGroups/models/tariffGroup';
import { TRANSLATE_MODALITY_TO_PT } from '../form/TariffGroupFields';
import { convertToDecimal, formatDateToLocaleDateString } from 'utils/form';

type Props = {
  tariffGroup: TariffGroupProps;
  onClickEditButton: (id: number) => void;
  onClickRemoveButton: (id: number) => void;
};

export default function TariffGroupsTableItem({
  tariffGroup: {
    id,
    powerDistributionUnit,
    tariffGroupConsumptionCosts,
    tariffGroupConsumptionCosts: {
      energyUsageTariffOnPeak,
      energyUsageTariffOffPeak,
      energyDistributionServiceTariffOnPeak,
      energyDistributionServiceTariffOffPeak,
      energyDistributionServiceTariff,
      energyUsageTariff,
    },
    tariffGroupDemandCosts,
    tariffGroupConsumptionGroup,
    tariffGroupModality,
    tariffGroupValidityEndDate,
    tariffGroupValidityStartDate,
  },
  onClickEditButton,
  onClickRemoveButton,
}: Props) {
  const handleClickEditButton = (e: SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    onClickEditButton(id);
  };

  const handleClickRemoveButton = (e: SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    onClickRemoveButton(id);
  };

  const tariffGroupDemands = [
    tariffGroupDemandCosts?.onPeak &&
      `PONTA ${convertToDecimal(tariffGroupDemandCosts.onPeak)}`,
    tariffGroupDemandCosts?.offPeak &&
      `FORA PONTA ${convertToDecimal(tariffGroupDemandCosts.offPeak)}`,
    tariffGroupDemandCosts?.all &&
      `NA ${convertToDecimal(tariffGroupDemandCosts.all)}`,
    !tariffGroupDemandCosts ? ' - ' : null,
  ];

  const tariffGroupConsumptionCostsEnergyUsage = [
    tariffGroupConsumptionCosts.energyUsageTariff &&
      `${convertToDecimal(energyUsageTariff)}`,
    tariffGroupConsumptionCosts.energyUsageTariffOnPeak &&
      `PONTA ${convertToDecimal(energyUsageTariffOnPeak)}`,
    tariffGroupConsumptionCosts.energyUsageTariffOffPeak &&
      `FORA PONTA ${convertToDecimal(energyUsageTariffOffPeak)}`,
  ];

  const tariffGroupConsumptionCostsEnergyDistributionService = [
    tariffGroupConsumptionCosts.energyDistributionServiceTariff &&
      `${convertToDecimal(energyDistributionServiceTariff)}`,
    tariffGroupConsumptionCosts.energyDistributionServiceTariffOnPeak &&
      `PONTA ${convertToDecimal(energyDistributionServiceTariffOnPeak)}`,
    tariffGroupConsumptionCosts.energyDistributionServiceTariffOffPeak &&
      `FORA PONTA ${convertToDecimal(energyDistributionServiceTariffOffPeak)}`,
  ];

  return (
    <TableBodyRow dataTestId={`tariff-group-row-id-${id}`}>
      <TableBodyColumn className="font-normal">
        {powerDistributionUnit.powerDistributionUnitLegalName}
      </TableBodyColumn>
      <TableBodyColumn className="font-normal">
        {`${formatDateToLocaleDateString(
          tariffGroupValidityStartDate as unknown as string
        )} - ${formatDateToLocaleDateString(
          tariffGroupValidityEndDate as unknown as string
        )}
        `}
      </TableBodyColumn>
      <TableBodyColumn className="font-normal">
        {tariffGroupConsumptionGroup}
      </TableBodyColumn>
      <TableBodyColumn className="font-normal">
        {TRANSLATE_MODALITY_TO_PT[tariffGroupModality]}
      </TableBodyColumn>
      <TableBodyColumn className="font-normal">
        {tariffGroupConsumptionCostsEnergyUsage.map(
          (item, index) => item && <TariffGroupItem key={index} value={item} />
        )}
      </TableBodyColumn>
      <TableBodyColumn className="font-normal">
        {tariffGroupConsumptionCostsEnergyDistributionService.map(
          (item, index) => item && <TariffGroupItem key={index} value={item} />
        )}
      </TableBodyColumn>
      <TableBodyColumn>
        {tariffGroupDemands.map(
          (item, index) =>
            item && (
              <TariffGroupItem
                key={index}
                value={item}
                className={classNames({ 'text-center': !tariffGroupDemandCosts })}
              />
            )
        )}
      </TableBodyColumn>
      <TableBodyColumn className="flex gap-x-3 items-end justify-end">
        <EditButton onClick={handleClickEditButton} />
        <RemoveButton onClick={handleClickRemoveButton} />
      </TableBodyColumn>
    </TableBodyRow>
  );
}
